// services/documentManagerService.js
// Full Document Management Service — Updated with firmId support

import { db, storage } from '../firebase-config';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ============================================================================
// CONSTANTS
// ============================================================================

export const DOCUMENT_CATEGORIES = {
  pleading:       { label: 'Pleading',        color: '#ef4444', icon: '⚖️' },
  motion:         { label: 'Motion',          color: '#f59e0b', icon: '📋' },
  contract:       { label: 'Contract',        color: '#10b981', icon: '📝' },
  correspondence: { label: 'Correspondence',  color: '#3b82f6', icon: '✉️' },
  evidence:       { label: 'Evidence',        color: '#8b5cf6', icon: '🔍' },
  court_order:    { label: 'Court Order',     color: '#ec4899', icon: '🏛️' },
  discovery:      { label: 'Discovery',       color: '#06b6d4', icon: '📑' },
  brief:          { label: 'Brief',           color: '#667eea', icon: '📖' },
  will_estate:    { label: 'Will / Estate',   color: '#d97706', icon: '📜' },
  corporate:      { label: 'Corporate',       color: '#059669', icon: '🏢' },
  financial:      { label: 'Financial',       color: '#dc2626', icon: '💰' },
  other:          { label: 'Other',           color: '#6b7280', icon: '📄' },
};

export const PRACTICE_AREAS = [
  'Criminal Defense', 'Corporate Law', 'Estate Planning', 'Labor Law',
  'Family Law', 'Civil Litigation', 'Real Estate', 'Intellectual Property',
];

// ============================================================================
// AUTO-CLASSIFICATION
// ============================================================================

export const classifyDocument = (filename, description = '') => {
  const lower = (filename + ' ' + description).toLowerCase();

  if (lower.match(/motion|move|movant/))                          return 'motion';
  if (lower.match(/pleading|complaint|petition|summons|writ/))    return 'pleading';
  if (lower.match(/contract|agreement|mou|memorandum of under/))  return 'contract';
  if (lower.match(/letter|email|correspondence|memo/))            return 'correspondence';
  if (lower.match(/evidence|exhibit|photo|video|recording/))      return 'evidence';
  if (lower.match(/order|ruling|judgment|decree|verdict/))        return 'court_order';
  if (lower.match(/discovery|interrogator|deposition|subpoena/))  return 'discovery';
  if (lower.match(/brief|argument|submission/))                   return 'brief';
  if (lower.match(/will|testament|estate|trust|probate/))         return 'will_estate';
  if (lower.match(/corporate|incorporation|bylaws|resolution/))   return 'corporate';
  if (lower.match(/invoice|receipt|financial|billing|payment/))   return 'financial';
  return 'other';
};

// Phase B, Part 15 — lightweight documentType classification. Reuses the
// existing keyword classifier above (no separate/bounded LLM call — "keep
// it cheap") for the ambiguous cases, but trusts the file FORMAT directly
// (a much stronger signal than filename keywords) for images/spreadsheets.
const CATEGORY_TO_DOCUMENT_TYPE = {
  motion: 'MOTION_APPLICATION', pleading: 'PLEADING', contract: 'CONTRACT',
  correspondence: 'LETTER', court_order: 'ORDER',
  evidence: 'OTHER', discovery: 'OTHER', brief: 'OTHER', will_estate: 'OTHER', corporate: 'OTHER', financial: 'OTHER', other: 'OTHER',
};
export const classifyDocumentType = (category, extension) => {
  const ext = (extension || '').toLowerCase();
  if (['jpg', 'jpeg', 'png'].includes(ext)) return { documentType: 'IMAGE_EXHIBIT', documentTypeSource: 'FORMAT', documentTypeConfidenceCategory: 'HIGH' };
  if (ext === 'xlsx' || ext === 'csv') return { documentType: 'SPREADSHEET', documentTypeSource: 'FORMAT', documentTypeConfidenceCategory: 'HIGH' };
  const mapped = CATEGORY_TO_DOCUMENT_TYPE[category] || 'OTHER';
  return { documentType: mapped, documentTypeSource: 'HEURISTIC', documentTypeConfidenceCategory: mapped === 'OTHER' ? 'LOW' : 'MEDIUM' };
};

// ============================================================================
// UPLOAD
// ============================================================================

// SHA-256 of the raw file bytes (Phase A, Part 17) — used for firm+matter
// -scoped duplicate detection. Never compared across firms or matters.
export const computeFileChecksum = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * @param {File} file
 * @param {string} userId
 * @param {object} metadata - caseId/caseName/description/tags/... plus
 *   `textContent` (kept for backward compat with any caller not yet passing
 *   `extraction`) and `allowDuplicate` to bypass the same-matter duplicate check.
 * @param {(pct:number)=>void} onProgress
 * @param {string} firmId
 * @param {object|null} extraction - the structured result from
 *   claudeService.js's extractTextFromFileDetailed: {text, status,
 *   extractionMethod, ocrStatus, failureReason, pageCount, extension}.
 *   Optional for backward compatibility — when absent, processingStatus is
 *   inferred from metadata.textContent alone (legacy behavior).
 */
export const uploadManagedDocument = async (file, userId, metadata = {}, onProgress, firmId, extraction = null) => {
  try {
    if (!firmId) {
      return { success: false, error: 'Missing firmId — cannot upload without firm context' };
    }

    const checksum = await computeFileChecksum(file);

    // Server-authorised destination (Hales Solicitors incident — recurrence
    // prevention). The browser no longer chooses the tenant: the server
    // derives the caller's ACTIVE firm, verifies the case belongs to it and
    // returns the only valid destination prefix. A session holding a stale
    // firm (membership changed since the page loaded) fails closed instead
    // of writing into another firm's namespace. Storage + Firestore rules
    // enforce the same invariant independently.
    // Also: same-firm + same-matter duplicate detection (Phase A, Part 17).
    let grant;
    try {
      const fns = getFunctions();
      grant = (await httpsCallable(fns, 'authorizeManagedDocumentUpload')({
        caseId: metadata.caseId || null, clientFirmId: firmId, checksum: metadata.allowDuplicate ? null : checksum,
      })).data;
    } catch (authErr) {
      const code = authErr?.details?.code || null;
      if (code === 'TENANT_CONTEXT_STALE' && typeof window !== 'undefined') {
        try { window.dispatchEvent(new CustomEvent('firmassist:tenant-context-stale')); } catch (_) { /* best effort */ }
      }
      return { success: false, error: authErr?.message || 'Upload not authorised.', code };
    }
    const serverFirmId = grant?.firmId;
    if (!serverFirmId || serverFirmId !== firmId || typeof grant.uploadPrefix !== 'string' || !grant.uploadPrefix.startsWith(`firms/${serverFirmId}/managed_docs/`)) {
      return { success: false, error: 'Upload not authorised for this firm.', code: 'TENANT_CONTEXT_STALE' };
    }
    if (!metadata.allowDuplicate && grant.duplicate) {
      return { success: false, error: 'DUPLICATE', duplicate: true, existingDocumentId: grant.existingDocumentId, message: 'This document already exists in this matter.' };
    }

    const timestamp = Date.now();
    const safeName  = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `${grant.uploadPrefix}${timestamp}_${safeName}`;
    const storageRef  = ref(storage, storagePath);

    const category = metadata.category || classifyDocument(file.name, metadata.description);
    const textContent = extraction ? extraction.text : (metadata.textContent || '');
    const ext = extraction?.extension || (file.name.includes('.') ? file.name.toLowerCase().split('.').pop() : '');
    // A document is READY only when it carries usable text — Storage upload
    // succeeding alone is UPLOADED, not READY (Part 8).
    const processingStatus = extraction
      ? extraction.status
      : (textContent && textContent.trim().length > 20 ? 'READY' : 'UPLOADED');
    const { documentType, documentTypeSource, documentTypeConfidenceCategory } = classifyDocumentType(category, ext);

    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          if (onProgress) onProgress(pct);
        },
        (error) => reject({ success: false, error: error.message }),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const docData = {
            name:         file.name,
            size:         file.size,
            type:         file.type,
            extension:    ext,
            category,
            practiceArea: metadata.practiceArea || '',
            caseId:       metadata.caseId || '',
            caseName:     metadata.caseName || '',
            description:  metadata.description || '',
            tags:         metadata.tags || [],
            jurisdiction: metadata.jurisdiction || '',
            court:        metadata.court || '',
            storagePath,
            downloadURL,
            checksum,
            uploadedBy:   userId,
            userId:       userId,
            firmId:       serverFirmId, // server-derived; identical to the storagePath's firm
            uploadedAt:   serverTimestamp(),
            lastModified: serverTimestamp(),
            version:      1,
            starred:      false,
            archived:     false,
            // Store truncated text content for AI case context (max 15k chars)
            textContent:  textContent ? textContent.substring(0, 15000) : '',
            processingStatus,
            extractionMethod: extraction?.extractionMethod || null,
            ocrStatus:    extraction?.ocrStatus || 'NOT_REQUIRED',
            failureReason: extraction?.failureReason || null,
            pageCount:    extraction?.pageCount ?? null,
            // Phase B — document type classification (Part 15) is pure
            // metadata on the parent doc, safe to set immediately. Structure
            // status/version/summary are set authoritatively by the
            // saveDocumentStructure call below (or left NOT_STARTED/
            // UNSUPPORTED here if there's no structure to save), so the
            // parent doc is never marked READY ahead of its actual subcollection write.
            documentType, documentTypeSource, documentTypeConfidenceCategory,
            structureVersion: extraction?.structureVersion || 1,
            structureStatus: extraction?.structure?.units?.length ? 'PROCESSING' : (extraction?.structureStatus || 'NOT_STARTED'),
            structuralExtractionMethod: extraction?.structure?.units?.length ? null : (extraction?.structuralExtractionMethod || null),
            structuralFailureReason: extraction?.structure?.units?.length ? null : (extraction?.structuralFailureReason || null),
          };

          const docRef = await addDoc(collection(db, 'managedDocuments'), docData);

          // Background Sonnet OCR re-index only when the synchronous
          // extraction pass explicitly signaled it needs one (PROCESSING /
          // ocrStatus PENDING) — never for a deterministic FAILED/UNSUPPORTED
          // outcome (Part 10: don't retry corrupt/password/unsupported files
          // endlessly), and never twice for the same reason.
          const isPdf = (file.type || '').includes('pdf') || file.name.toLowerCase().endsWith('.pdf');
          const needsBackgroundOcr = extraction
            ? (isPdf && docData.processingStatus === 'PROCESSING')
            : (isPdf && !docData.textContent); // legacy behavior when no `extraction` was supplied
          if (needsBackgroundOcr) {
            const fns = getFunctions();
            httpsCallable(fns, 'reindexDocumentText')({ docId: docRef.id })
              .catch(e => console.warn('[OCR] background OCR failed:', e.message));
          }

          // Phase B — persist client-computed structure (native PDF, DOCX,
          // XLSX, TXT/CSV/MD, image OCR all extract structure client-side;
          // see claudeService.js's extractTextFromFileDetailed). Routed
          // through the saveDocumentStructure callable rather than a direct
          // client write, since the /structure subcollection denies all
          // direct client access (firestore.rules, Part 24). Fire-and-forget,
          // same discipline as the OCR trigger above — a structure-save
          // hiccup must never block or fail the actual document upload.
          if (extraction?.structure?.units?.length) {
            const fns = getFunctions();
            httpsCallable(fns, 'saveDocumentStructure')({
              documentId: docRef.id,
              units: extraction.structure.units,
              summary: extraction.structure.summary,
              structureStatus: extraction.structureStatus,
              structuralExtractionMethod: extraction.structuralExtractionMethod,
              structuralFailureReason: extraction.structuralFailureReason,
              pageCount: extraction.pageCount,
            }).catch(e => console.warn('[Structure] save failed:', e.message));
          }

          resolve({ success: true, id: docRef.id, downloadURL, document: { id: docRef.id, ...docData } });
        },
      );
    });
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// READ (with client-side filters)
// ============================================================================

// Tenancy Integrity Incident (Phase 1 containment) — managedDocuments'
// direct client-read rule trusted only the document's own firmId, never
// confirming its claimed caseId actually belonged to that firm. Reads now
// go through the listManagedDocuments callable (server-derives firmId,
// re-validates case tenancy per document) instead of a direct Firestore
// query; firestore.rules denies direct client reads of this collection
// outright. userId/firmId parameters below are kept only so every existing
// call site's signature is unchanged — the server derives its own identity
// from the authenticated caller regardless of what's passed here.
//
// Firestore Timestamp fields can't survive the callable boundary as class
// instances (the server sends ISO strings) — this shim restores a
// .toDate()/.toMillis() surface on every ISO-8601-shaped string field so
// every existing caller across the app that does `doc.someTimestamp.toDate()`
// keeps working unchanged.
const ISO_8601_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
function reviveTimestampShims(value) {
  if (typeof value === 'string' && ISO_8601_RE.test(value)) {
    const d = new Date(value);
    return { toDate: () => d, toMillis: () => d.getTime(), __isoString: value };
  }
  if (Array.isArray(value)) return value.map(reviveTimestampShims);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = reviveTimestampShims(v);
    return out;
  }
  return value;
}

// Word upgrade Stage 1a — extracted text is fetched explicitly (access-checked,
// at most 10 documents per call) instead of arriving in every listing.
export const TEXT_HYDRATION_LIMIT = 10;
const TEXT_FIELDS = ['textContent', 'extractedText', 'content'];

/** Returns Map(documentId → { field, text, truncated }) for documents the caller may see. */
export const fetchDocumentTexts = async (documentIds) => {
  const ids = [...new Set((documentIds || []).filter(Boolean))].slice(0, TEXT_HYDRATION_LIMIT);
  if (!ids.length) return new Map();
  const result = await httpsCallable(getFunctions(), 'getDocumentTexts')({ documentIds: ids });
  return new Map((result.data?.texts || []).map((t) => [t.documentId, { field: t.field, text: t.text, truncated: t.truncated === true }]));
};

/**
 * Puts fetched text back on listed documents in the SAME field it was stored
 * in (textContent, extractedText or content), so every consumer behaves as it
 * did when listings carried text. Failure leaves documents without text —
 * consumers then fall back to the file itself, as for documents with no
 * stored text.
 */
export const hydrateDocumentTexts = async (documents) => {
  const docs = (documents || []).filter((d) => d && d.id && d.textAvailable);
  if (!docs.length) return documents;
  try {
    const texts = await fetchDocumentTexts(docs.map((d) => d.id));
    for (const d of docs) {
      const t = texts.get(d.id);
      if (t && typeof t.text === 'string' && t.text && TEXT_FIELDS.includes(t.field)) d[t.field] = t.text;
    }
  } catch (error) {
    console.error('[getDocumentTexts] failed:', error.code || '(no code)', error.message);
  }
  return documents;
};

export const getManagedDocuments = async (userId, filters = {}, firmId) => {
  try {
    const fns = getFunctions();
    // With a known matter, the server returns ONLY that matter's documents
    // (case-scoped listing) — never the firm-wide list filtered here.
    // metadataOnly (opt-in) omits extracted text of file-backed documents.
    const payload = filters.caseId ? { caseId: filters.caseId, ...(filters.metadataOnly ? { metadataOnly: true } : {}) } : {};
    const result = await httpsCallable(fns, 'listManagedDocuments')(payload);
    let documents = (result.data?.documents || []).map(reviveTimestampShims);

    // — client-side filters —
    if (filters.category && filters.category !== 'all') {
      documents = documents.filter((d) => d.category === filters.category);
    }
    if (filters.caseId) {
      // Defence in depth: a document is only ever returned for the matter it names.
      documents = documents.filter((d) => d.caseId === filters.caseId);
    }
    if (filters.practiceArea && filters.practiceArea !== 'all') {
      documents = documents.filter((d) => d.practiceArea === filters.practiceArea);
    }
    if (filters.starred) {
      documents = documents.filter((d) => d.starred);
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      documents = documents.filter((d) =>
        d.name?.toLowerCase().includes(s) ||
        d.description?.toLowerCase().includes(s) ||
        d.caseName?.toLowerCase().includes(s) ||
        d.tags?.some((t) => t.toLowerCase().includes(s)),
      );
    }

    // newest first
    documents.sort((a, b) => {
      const da = a.uploadedAt?.toDate?.() || new Date(0);
      const db2 = b.uploadedAt?.toDate?.() || new Date(0);
      return db2 - da;
    });

    // Word upgrade Stage 1a: listings no longer carry extracted text. A
    // matter-scoped list that is not metadata-only (the Paralegal's case
    // document flows) gets the stored text of its newest documents through the
    // access-checked getDocumentTexts callable, so those flows keep working.
    if (filters.caseId && !filters.metadataOnly) {
      await hydrateDocumentTexts(documents.filter((d) => d.textAvailable).slice(0, TEXT_HYDRATION_LIMIT));
    }

    return { success: true, documents };
  } catch (error) {
    console.error('[getManagedDocuments] listManagedDocuments call failed:', error.code || '(no code)', error.message);
    return { success: false, error: error.message, documents: [] };
  }
};

// ============================================================================
// UPDATE / DELETE / STAR / ARCHIVE
// ============================================================================

export const updateManagedDocument = async (docId, updates) => {
  try {
    await updateDoc(doc(db, 'managedDocuments', docId), {
      ...updates,
      lastModified: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteManagedDocument = async (docId, storagePath) => {
  try {
    await deleteDoc(doc(db, 'managedDocuments', docId));
    if (storagePath) {
      try { await deleteObject(ref(storage, storagePath)); } catch (_) { /* file may already be gone */ }
    }
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const toggleStarDocument = async (docId, currentStarred) =>
  updateManagedDocument(docId, { starred: !currentStarred });

export const archiveManagedDocument = async (docId) =>
  updateManagedDocument(docId, { archived: true });

/**
 * Phase C2 — triggers the server-side document processing orchestrator
 * (functions/documentProcessingOrchestrator.js) to re-derive this document's
 * text/structure/index entirely from its canonical Storage object, for any
 * supported format (not just PDF). The browser INITIATES this; it does not
 * itself parse anything (Part 9 — server is the authoritative parser).
 */
export const reprocessManagedDocument = async (docId) => {
  try {
    const fns = getFunctions();
    const result = await httpsCallable(fns, 'reprocessDocumentStructure')({ documentId: docId });
    return { success: true, ...result.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// HELPERS
// ============================================================================

export const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// ============================================================================
// DOCUMENT TEMPLATES
// ============================================================================

export const DOCUMENT_TEMPLATES = [
  {
    id: 'motion_dismiss',
    name: 'Motion to Dismiss',
    category: 'motion',
    practiceArea: 'Civil Litigation',
    description: 'Standard motion to dismiss template with caption, arguments, and certificate of service.',
    content: `MOTION TO DISMISS\n\nIN THE [COURT NAME]\n[JURISDICTION]\n\n[PLAINTIFF NAME],\n    Plaintiff,\n        v.                          Case No: [CASE NUMBER]\n[DEFENDANT NAME],\n    Defendant.\n\nMOTION TO DISMISS\n\nNOW COMES the Defendant, [DEFENDANT NAME], by and through undersigned counsel, and respectfully moves this Honorable Court to dismiss the above-captioned action pursuant to [RULE/STATUTE], and in support thereof states as follows:\n\nI. INTRODUCTION\n[Brief overview]\n\nII. STATEMENT OF FACTS\n1. [Fact 1]\n2. [Fact 2]\n\nIII. LEGAL STANDARD\n[Applicable standard]\n\nIV. ARGUMENT\nA. [First argument]\nB. [Second argument]\n\nV. CONCLUSION\nWHEREFORE, Defendant respectfully requests that this Court dismiss the Plaintiff's complaint with prejudice.\n\nRespectfully submitted,\n\n_________________________\n[ATTORNEY NAME]\n[BAR NUMBER]\n[FIRM NAME]\n[ADDRESS]\n[PHONE] | [EMAIL]\n\nCERTIFICATE OF SERVICE\nI hereby certify that on [DATE], a true and correct copy of the foregoing was served upon all counsel of record.\n\n_________________________\n[ATTORNEY NAME]`,
  },
  {
    id: 'client_engagement',
    name: 'Client Engagement Letter',
    category: 'correspondence',
    practiceArea: 'Corporate Law',
    description: 'Professional engagement letter outlining scope, fees, retainer, and termination.',
    content: `CLIENT ENGAGEMENT LETTER\n\n[DATE]\n\n[CLIENT NAME]\n[CLIENT ADDRESS]\n\nRe: Engagement of Legal Services\n\nDear [CLIENT NAME],\n\nThank you for selecting [FIRM NAME] to represent you in connection with [MATTER DESCRIPTION]. This letter sets forth the terms of our engagement.\n\n1. SCOPE OF REPRESENTATION\nWe will represent you in [specific scope].\n\n2. FEES AND BILLING\n- Partners: $[RATE]/hour\n- Associates: $[RATE]/hour\n- Paralegals: $[RATE]/hour\n\n3. RETAINER\nAn initial retainer of $[AMOUNT] is required.\n\n4. EXPENSES\nYou will be responsible for all costs and expenses incurred.\n\n5. COMMUNICATION\nWe will keep you informed of significant developments.\n\n6. TERMINATION\nEither party may terminate this engagement with written notice.\n\nSincerely,\n\n_________________________\n[ATTORNEY NAME]\n[FIRM NAME]\n\nACCEPTED AND AGREED:\n\n_________________________\n[CLIENT NAME]\nDate: ___________`,
  },
  {
    id: 'contract_general',
    name: 'General Contract / Agreement',
    category: 'contract',
    practiceArea: 'Corporate Law',
    description: 'Multi-purpose contract template with standard clauses.',
    content: `AGREEMENT\n\nThis Agreement is entered into as of [DATE], by and between:\n\nParty A: [NAME AND ADDRESS]\nParty B: [NAME AND ADDRESS]\n\nRECITALS\nWHEREAS, [background context];\n\nNOW, THEREFORE, in consideration of the mutual covenants herein, the parties agree:\n\n1. SCOPE OF AGREEMENT\n[Define scope]\n\n2. TERM\nThis Agreement commences on [DATE] and continues until [DATE/CONDITION].\n\n3. PAYMENT TERMS\n[Payment details]\n\n4. OBLIGATIONS OF PARTY A\n[List obligations]\n\n5. OBLIGATIONS OF PARTY B\n[List obligations]\n\n6. CONFIDENTIALITY\n[Confidentiality terms]\n\n7. TERMINATION\n[Termination conditions]\n\n8. GOVERNING LAW\nThis Agreement is governed by the laws of [JURISDICTION].\n\n9. ENTIRE AGREEMENT\nThis constitutes the entire agreement between the parties.\n\nIN WITNESS WHEREOF:\n\nPARTY A:                        PARTY B:\n_________________________    _________________________\nName:                          Name:\nDate:                          Date:`,
  },
  {
    id: 'discovery_request',
    name: 'Discovery Request',
    category: 'discovery',
    practiceArea: 'Civil Litigation',
    description: 'First set of interrogatories and requests for production.',
    content: `DISCOVERY REQUEST\n\nIN THE [COURT NAME]\n[JURISDICTION]\n\n[PLAINTIFF] v. [DEFENDANT]\nCase No: [CASE NUMBER]\n\n[PARTY]'S FIRST SET OF INTERROGATORIES\n\nTO: [OPPOSING PARTY]\n\nPursuant to [RULE], you are requested to answer the following under oath:\n\nINTERROGATORY NO. 1:\n[Question]\n\nINTERROGATORY NO. 2:\n[Question]\n\nINTERROGATORY NO. 3:\n[Question]\n\nREQUEST FOR PRODUCTION OF DOCUMENTS\n\nREQUEST NO. 1:\n[Description of documents requested]\n\nREQUEST NO. 2:\n[Description of documents requested]\n\nRespectfully submitted,\n\n_________________________\n[ATTORNEY NAME]\n[BAR NUMBER]`,
  },
  {
    id: 'will_simple',
    name: 'Simple Will',
    category: 'will_estate',
    practiceArea: 'Estate Planning',
    description: 'Basic last will and testament with standard articles.',
    content: `LAST WILL AND TESTAMENT OF [TESTATOR NAME]\n\nI, [TESTATOR NAME], of [CITY, STATE/REGION], being of sound mind and memory, do hereby declare this to be my Last Will and Testament, revoking all previous wills and codicils.\n\nARTICLE I — FAMILY\nI am [married to / single]. My children are:\n1. [Child name, DOB]\n2. [Child name, DOB]\n\nARTICLE II — DEBTS\nI direct my Executor to pay all legally enforceable debts and funeral expenses.\n\nARTICLE III — SPECIFIC BEQUESTS\n1. [Specific item] to [beneficiary]\n2. [Specific item] to [beneficiary]\n\nARTICLE IV — RESIDUARY ESTATE\nI give the residue of my estate to [beneficiary].\n\nARTICLE V — EXECUTOR\nI appoint [NAME] as Executor. Alternate: [NAME].\n\nARTICLE VI — GUARDIAN (if applicable)\nI appoint [NAME] as guardian of my minor children.\n\nIN WITNESS WHEREOF, I have signed this Will on [DATE].\n\n_________________________\n[TESTATOR NAME]\n\nWITNESSES:\n_________________________ | _________________________\nWitness 1                   Witness 2`,
  },
  {
    id: 'legal_memo',
    name: 'Internal Legal Memorandum',
    category: 'brief',
    practiceArea: 'Civil Litigation',
    description: 'IRAC-style internal memo for case analysis.',
    content: `LEGAL MEMORANDUM\n\nTO:      [Senior Attorney / Partner]\nFROM:    [Author]\nDATE:    [DATE]\nRE:      [SUBJECT — Client Name / Matter]\n\nI. QUESTION PRESENTED\n[State the legal question concisely]\n\nII. SHORT ANSWER\n[Brief answer — 2-3 sentences]\n\nIII. STATEMENT OF FACTS\n[Relevant facts in chronological order]\n\nIV. DISCUSSION\nA. [Rule / Legal Standard]\n[Cite authority]\n\nB. [Application to Facts]\n[Apply rule to facts]\n\nC. [Counter-arguments]\n[Address opposing view]\n\nV. CONCLUSION\n[Summarize and recommend next steps]`,
  },
];